// Global Instructions Rule Applied!
//
// Configuration → Account Owner Roles (cfg.account_owner_roles). Admin-gated
// lookup CRUD in the PipelineStatusesPanel shape: title row + New button,
// antd Table, AntD Modal with the default footer. Delete is refused server-
// side while the role has current assignments.
import React, { useCallback, useEffect, useState } from 'react'
import { Button, Input, InputNumber, Modal, Popconfirm, Switch, Table, Tag, Typography } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { AccountOwnerRoleAdminRow } from '../../../state_manager/data_models/relationships_model.ts'
import { fetchAccountOwnerRoles, saveAccountOwnerRole, deleteAccountOwnerRole } from '../../relationships/services/relationshipsService.ts'

const { Title } = Typography

export default function AccountOwnerRolesPanel() {
  const [rows, setRows] = useState<AccountOwnerRoleAdminRow[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<AccountOwnerRoleAdminRow | null>(null)
  const [roleKey, setRoleKey] = useState('')
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [isExclusive, setIsExclusive] = useState(false)
  const [sortOrder, setSortOrder] = useState<number>(100)
  const [submitting, setSubmitting] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRows(await fetchAccountOwnerRoles(token()))
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load owner roles')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openAdd = () => {
    setEditing(null); setRoleKey(''); setLabel(''); setDescription(''); setIsExclusive(false); setSortOrder(100); setOpen(true)
  }
  const openEdit = (r: AccountOwnerRoleAdminRow) => {
    setEditing(r); setRoleKey(r.contact_relationship_titles__title_key ?? ''); setLabel(r.contact_relationship_titles__name)
    setDescription(r.contact_relationship_titles__description ?? ''); setIsExclusive(r.contact_relationship_titles__is_exclusive)
    setSortOrder(r.contact_relationship_titles__sort_order ?? 100); setOpen(true)
  }

  const submit = async () => {
    setSubmitting(true)
    try {
      await saveAccountOwnerRole(
        editing ? editing.contact_relationship_titles__id : null,
        { title_key: roleKey.trim(), name: label.trim(), description: description.trim() || null, is_exclusive: isExclusive, sort_order: sortOrder },
        token()
      )
      toast.success(editing ? 'Role updated' : 'Role added')
      setOpen(false)
      await load()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  const onDelete = async (r: AccountOwnerRoleAdminRow) => {
    setDeletingId(r.contact_relationship_titles__id)
    try {
      await deleteAccountOwnerRole(r.contact_relationship_titles__id, token())
      toast.success('Role deleted')
      await load()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setDeletingId(null)
    }
  }

  const columns: ColumnsType<AccountOwnerRoleAdminRow> = [
    { title: 'Key', dataIndex: 'contact_relationship_titles__title_key', key: 'key', width: 170, render: (v: string | null) => <code>{v ?? ''}</code> },
    { title: 'Label', dataIndex: 'contact_relationship_titles__name', key: 'label', width: 180 },
    { title: 'Description', dataIndex: 'contact_relationship_titles__description', key: 'desc', ellipsis: true, render: (v: string | null) => v || '' },
    { title: 'Exclusive', dataIndex: 'contact_relationship_titles__is_exclusive', key: 'excl', width: 100, render: (v: boolean) => (v ? <Tag color='blue'>one holder</Tag> : '') },
    { title: 'Order', dataIndex: 'contact_relationship_titles__sort_order', key: 'order', width: 80 },
    { title: 'In use', dataIndex: 'prog__usage_count', key: 'usage', width: 80 },
    {
      title: '', key: 'actions', width: 90, align: 'right',
      render: (_: unknown, r) => (
        <span className='inline-flex gap-1'>
          <Button type='text' size='small' icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title='Delete this role?' okText='Delete' onConfirm={() => onDelete(r)} disabled={r.prog__usage_count > 0}>
            <Button type='text' danger size='small' icon={<DeleteOutlined />} disabled={r.prog__usage_count > 0} loading={deletingId === r.contact_relationship_titles__id} />
          </Popconfirm>
        </span>
      )
    }
  ]

  return (
    <div className='flex flex-col h-full min-h-0'>
      <div className='flex items-center justify-between flex-wrap gap-2 mb-3'>
        <Title level={4} style={{ margin: 0 }}>Account Owner Roles</Title>
        <Button type='primary' icon={<PlusOutlined />} onClick={openAdd}>New Role</Button>
      </div>
      <div className='flex-1 min-h-0 overflow-auto'>
        <Table size='small' rowKey='contact_relationship_titles__id' columns={columns} dataSource={rows} loading={loading} pagination={false} />
      </div>
      <Modal title={editing ? 'Edit role' : 'New role'} open={open} okText={editing ? 'Save' : 'Add'}
        okButtonProps={{ disabled: !label.trim() || (!editing && !roleKey.trim()) }} confirmLoading={submitting}
        onOk={submit} onCancel={() => setOpen(false)} destroyOnClose>
        <div className='flex flex-col gap-3 pt-2'>
          <div><div className='text-xs text-gray-600 mb-1'>Key (snake_case; fixed once created)</div>
            <Input value={roleKey} onChange={(e) => setRoleKey(e.target.value)} disabled={!!editing} placeholder='e.g. primary_rm' /></div>
          <div><div className='text-xs text-gray-600 mb-1'>Label</div><Input value={label} onChange={(e) => setLabel(e.target.value)} /></div>
          <div><div className='text-xs text-gray-600 mb-1'>Description</div><Input.TextArea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          <div className='flex items-center gap-6'>
            <span className='inline-flex items-center gap-2 text-sm'><Switch checked={isExclusive} onChange={setIsExclusive} /> One current holder per account</span>
          </div>
          <div><div className='text-xs text-gray-600 mb-1'>Sort order</div><InputNumber value={sortOrder} onChange={(v) => setSortOrder(Number(v ?? 100))} /></div>
        </div>
      </Modal>
    </div>
  )
}
